import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { registerWhatsAppAccount } from "../whatsapp/whatsappApi";

const META_WA_START = "https://developers.facebook.com/docs/whatsapp/cloud-api/get-started";

export default function InboxChannelSetupModal({
  open,
  onClose,
  businessId,
  apiBaseUrl,
  onSuccess,
}) {
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [webhookVerifyToken, setWebhookVerifyToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
  }, [open]);

  const webhookUrl = `${apiBaseUrl}/api/whatsapp/webhook`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
    } catch {
      /* ignore */
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = {
        business_id: businessId,
        phone_number_id: phoneNumberId.trim(),
        access_token: accessToken.trim(),
        webhook_verify_token: webhookVerifyToken.trim(),
      };
      if (appSecret.trim()) body.app_secret = appSecret.trim();
      if (displayPhoneNumber.trim()) body.display_phone_number = displayPhoneNumber.trim();

      const res = await registerWhatsAppAccount(body);
      if (res.error) {
        setError(typeof res.error === "string" ? res.error : JSON.stringify(res.error));
        return;
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err?.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700 }}>Configure messaging channels</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add your Meta WhatsApp Cloud API credentials. Use the same verify token in Meta&apos;s webhook settings as
            below.
          </Typography>

          <Alert severity="info" sx={{ mb: 2, alignItems: "flex-start" }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Callback URL (paste in Meta → WhatsApp → Configuration):
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: "action.hover",
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 12,
                wordBreak: "break-all",
              }}
            >
              <span style={{ flex: 1 }}>{webhookUrl}</span>
              <IconButton size="small" onClick={handleCopy} aria-label="Copy webhook URL" type="button">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Alert>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            WhatsApp Cloud API
          </Typography>

          <TextField
            margin="dense"
            label="Phone number ID"
            fullWidth
            required
            value={phoneNumberId}
            onChange={(e) => setPhoneNumberId(e.target.value)}
            helperText="From Meta App → WhatsApp → API setup"
          />
          <TextField
            margin="dense"
            label="Access token"
            fullWidth
            required
            type="password"
            autoComplete="new-password"
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Webhook verify token"
            fullWidth
            required
            value={webhookVerifyToken}
            onChange={(e) => setWebhookVerifyToken(e.target.value)}
            helperText="Choose a secret string; enter the same value in Meta webhook settings"
          />
          <TextField
            margin="dense"
            label="App secret (optional)"
            fullWidth
            type="password"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            helperText="For X-Hub-Signature-256 verification"
          />
          <TextField
            margin="dense"
            label="Display phone number (optional)"
            fullWidth
            value={displayPhoneNumber}
            onChange={(e) => setDisplayPhoneNumber(e.target.value)}
            placeholder="+1..."
          />

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Typography variant="body2" sx={{ mt: 2 }}>
            <Link href={META_WA_START} target="_blank" rel="noopener noreferrer">
              Meta WhatsApp Cloud API guide
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />
            </Link>
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Email and SMS use separate integrations (IMAP/SMTP, Twilio). Configure those via your deployment when
            available.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting} sx={{ bgcolor: "#7c3aed" }}>
            {submitting ? "Saving…" : "Save WhatsApp"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
